<template>
  <div
    @click="emits('click', taskInfo)"
    :class="[
      'task-item relative box-border px-[10px] py-[6px] gap-[4px] rounded-[6px] hover:bg-[#f5f4ff] hover:text-[#314dfb] flex duration-150 items-center cursor-pointer',
      {
        'highlight-item': isHighlight
      }
    ]"
  >
    <div
      class="cursor-pointer flex-1 z-[9] whitespace-nowrap overflow-hidden flex flex-grow border-box shrink-0 text-[14px]"
    >
      <div class="flex items-center gap-[8px] w-full" v-if="isRename">
        <form @submit.prevent="saveNewName()">
          <el-input
            v-model="newTaskName"
            @keydown.esc="clearEditInfo"
            @click.stop
            :autofocus="true"
            placeholder="请输入任务名称"
            type="text"
          ></el-input>
        </form>
        <div class="flex gap-[4px] shrink-0">
          <el-icon class="cursor-pointer" @click.stop="saveNewName()"><i-ep-check /></el-icon>
          <el-icon color="red" class="cursor-pointer" @click.stop="clearEditInfo"
            ><i-ep-close
          /></el-icon>
        </div>
      </div>

      <div class="flex overflow-hidden gap-[4px] items-center" v-else>
        <div
          :class="[
            'rounded-[4px] w-[26px] justify-center text-[9px] text-[#fff] px-[4px] py-[1px] flex items-center gap-[2px]'
          ]"
          :style="{
            backgroundColor: statusMapEnum[taskInfo.status]?.backgroundColor
          }"
        >
          <span v-if="statusMapEnum[taskInfo.status]?.text !== '生成中'">
            {{ statusMapEnum[taskInfo.status]?.text }}
          </span>
          <div
            class="rounded-[4px] w-[26px] justify-center text-[9px] text-[#fff] px-[4px] py-[1px] flex items-center gap-[2px]"
            v-else
          >
            <el-icon size="13" color="#fff" class="rotate"><i-ep-loading /></el-icon>
          </div>
        </div>
        <span>{{ taskInfo.name }}</span>
      </div>

      <div class="absolute top-0 right-0 bottom-0 left-0" v-if="isProcessing">
        <div class="is-in-edit-wrap absolute z-[1] w-full h-full flex justify-center items-center">
          <el-icon size="20" color="#b6b6b6" class="rotate"><i-ep-loading /></el-icon>
        </div>
        <div
          class="absolute flex justify-center items-center is-in-edit right-0 top-0 bottom-0 left-0 bg-white-0.8 z-[22]"
        ></div>
      </div>
    </div>

    <div class="show-task-item-edit">
      <TaskItemEdit
        :taskId="taskId"
        @rename="handleRename"
        @delete="handleDelete"
        @clone="handleClone"
      />
    </div>
  </div>
</template>
<script setup>
import { computed, ref, watch } from 'vue'
import { useTaskStore } from '@/store/taskStore'
import { useLayoutStore } from '../../store/layoutStore'
import { statusMapEnum } from '@/utils/enums'
import { taskClone, taskRename } from '@/service/task'
import { ElMessage } from 'element-plus'

const props = defineProps({
  taskId: {
    type: [String, Number],
    required: true,
    validator: (value) => {
      if (!value) {
        console.warn('taskId is required')
        return false
      }

      return true
    }
  }
})

const isRename = ref(false)
const newTaskName = ref('')
const isProcessing = ref(false)

const emits = defineEmits(['click'])

const taskStore = useTaskStore()
const layoutStore = useLayoutStore()

const clearEditInfo = () => {
  isRename.value = false
  newTaskName.value = ''
}

const taskInfo = computed(() => {
  return taskStore.rawTasks[props.taskId]
})

const isHighlight = computed(() => {
  return taskStore.currentTaskDetail.id === props.taskId
})

const handleRename = () => {
  isRename.value = true
  newTaskName.value = taskInfo.value.name // 重命名时，将任务名称赋值给输入框
}

const handleClone = async () => {
  if (taskInfo.value.status === '草稿') return // 草稿状态不允许克隆
  try {
    layoutStore.toggleSidebar(false) // 关闭侧边栏
    layoutStore.setShowResults(false) // 关闭结果页面
    layoutStore.setNewDialogLoadingRef(true)

    const res = await taskClone(taskInfo.value.id)
    const newTask = {
      ...taskStore.defaultTask,
      id: res.data.id,
      status: '草稿',
      name: taskInfo.value.name
    }
    taskStore.updateTaskList([newTask])
    layoutStore.toggleSidebar(true) // 开启侧边栏
    layoutStore.setDialogLoadingRef(true) // 打开侧边栏的loading
    taskStore.setCurrentTaskDetail(newTask) // 设置当前激活任务的详情
    taskStore.initializeTaskDetail(newTask.id) // 初始化任务详情
  } catch (error) {
    console.log(error)
  } finally {
    layoutStore.setNewDialogLoadingRef(false)
  }
}
watch(
  () => taskStore.currentTaskDetail.id,
  (newVal) => {
    if (newVal !== props.taskId) {
      clearEditInfo()
    }
  },
  { deep: true }
)

const saveNewName = async () => {
  try {
    isProcessing.value = true
    const { code } = await taskRename({
      id: taskInfo.value.id,
      name: newTaskName.value
    })

    if (code === 0) {
      taskStore.rawTasks[props.taskId].name = newTaskName.value // 更新任务列表

      taskStore.updateTaskDetail({ name: newTaskName.value }) // 更新任务详情
      ElMessage({
        message: '重命名成功',
        type: 'success'
      })
    } else {
      ElMessage({
        message: '重命名失败',
        type: 'error'
      })
    }
  } catch (error) {
    console.log(error)
  } finally {
    clearEditInfo()
    isProcessing.value = false
  }
}

const handleDelete = () => {
  layoutStore.setDeleteDialogVisible(true)
}
</script>
<style lang="scss" scoped>
.task-item {
  box-sizing: border-box;
  &:hover {
    :deep(.show-task-item-edit .edit) {
      opacity: 1;
      pointer-events: auto;
    }
  }
}
.show_edit {
  opacity: 1 !important;
  pointer-events: auto !important;
}
.is-in-edit-wrap {
  position: relative;
  backdrop-filter: blur(2px);
}

.highlight-item {
  background-color: #f5f4ff;
  color: #314dfb;
  position: sticky;
  top: 0;
  bottom: 0;
  z-index: 999;
  :deep(.show-task-item-edit .edit) {
    opacity: 1;
    pointer-events: auto;
  }
}
.rotate {
  animation: rotate 1s linear infinite;
}
@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
